import { useMemo } from "react";
import { Colors } from "../../../../utils/colors";
import { MassTextsActionItem } from "../Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import { Event, EventType, Visibility } from "@markit/common.types";
import VirtuosoList from "../../../Lists/VirtuosoList";
import { GetDateWithTime, isEventRelatedType } from "@markit/common.utils";
import EventItemImage from "../../../DisplayItem/EventItem/EventItemImage";
import EmptyStateButton from "../../../Buttons/EmptyStateButton";
import { getProductDetails } from "../../../../utils/productUtils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import EventItemPreviewType from "../../../DisplayItem/EventItem/EventItemPreviewType";
import { ActionItemHorizontalDivider } from "../../../Dividers/ActionItemHorizontalDivider";
import { getEventDisplayDate } from "../../../../utils/GetLongDate";

type AttachmentSelectItemListProps = {
  items: string[]; // The ID of the selected item(s)
  setItems: (newItems: string) => void;
  itemsToShow: Event[];
  stateValue: number;
  searchTerm: string;
  selectedEventType: EventType | undefined;
  showHiddenLabel: boolean;
  isTicketLink: boolean; // true for ticket link or calendar invite
};

// Shows the list of items to select for the attachment
const AttachmentSelectItemList = (props: AttachmentSelectItemListProps) => {
  const {
    items,
    setItems,
    itemsToShow,
    stateValue,
    searchTerm,
    selectedEventType,
    showHiddenLabel,
    isTicketLink,
  } = props;
  const { uid } = useSelector(getAccountState).account.accountData;

  const styles = {
    eventItemContainer: {
      border: "none",
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
    },
  };

  const renderHiddenLabel = useMemo(
    () =>
      showHiddenLabel ? (
        <div className="AlignedRow" style={{ gap: 4 }}>
          <Icon icon="ion:eye" height={14} style={{ color: Colors.ORANGE1 }} />
          <span style={{ color: Colors.ORANGE1 }}>Hidden</span>
        </div>
      ) : null,
    [showHiddenLabel]
  );

  return (
    <VirtuosoList
      items={itemsToShow}
      loadMoreOnPress={() => {}}
      renderItem={(item: Event) => {
        return (
          <div>
            <MassTextsActionItem
              title={item.title}
              subtext={
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <span>
                    {isEventRelatedType(item.eventType)
                      ? getEventDisplayDate(item)
                      : "Created: " + GetDateWithTime(item.createdAt, true)}
                  </span>
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    {(item.eventType === EventType.MARKIT &&
                      item.createdBy !== uid) ||
                    isTicketLink ? (
                      <EventItemPreviewType eventItem={item} />
                    ) : null}
                    {item.visibility === Visibility.Private
                      ? renderHiddenLabel
                      : null}
                  </div>
                </div>
              }
              icon={
                <EventItemImage
                  item={item}
                  size={48}
                  containerStyles={styles.eventItemContainer}
                />
              }
              onPress={() => setItems(item.id)}
              isCheckSelected={items.includes(item.id)}
              checkColor={Colors.BLACK}
            />
            <ActionItemHorizontalDivider dividerMargin={7} />
          </div>
        );
      }}
      isLoading={false}
      isFinished={true}
      searchTerm={searchTerm}
      renderNonSearchEmptyView={
        <EmptyStateButton
          title={
            isTicketLink
              ? "No Orders to Show"
              : `No ${stateValue === 0 ? "Active" : "Inactive"} ${
                  selectedEventType
                    ? getProductDetails(selectedEventType).title
                    : "Products"
                }`
          }
          description={
            isTicketLink
              ? "This person has no orders to any of your upcoming events or products"
              : "Your items will show up here"
          }
          icon={
            <Icon
              icon={"ion:calendar"}
              height={64}
              style={{ color: Colors.GRAY1 }}
            />
          }
          iconBox={84}
          containerStyles={{ paddingTop: 100 }}
        />
      }
      renderHeader={null}
    />
  );
};

export default AttachmentSelectItemList;
